import { Button } from '@components/Button.tsx';
import { ButtonType } from '@common/types/button';
import { type CustomDirectusTypes } from '@funnellab/shared/src/types/directus.ts';
import { IconType } from '@src/IconType.ts';
import { Link, useNavigate } from 'react-router-dom';
import {
  generateJobWizardUrl,
  generateJobWizardUrlFromFirstStep,
} from '@src/config/paths';
import { type organizationJobsQuery } from '@src/api/queries/oragnizationJobs.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TrashButton from '@components/TrashButton.tsx';
import jobService from '@src/api/services/JobService.ts';
import type { CellComponent, ItemTypeFromQuery } from '../table/types';
import { classNames } from '@utils/classNames.ts';
import { jobCopyFeatureFlag } from '@src/utils/featureFlags/jobCopyFeatureFlag';
import { useUserContext } from '@src/contexts/useUserContext';

export const RowActions: CellComponent<
  ItemTypeFromQuery<CustomDirectusTypes, 'job', typeof organizationJobsQuery>,
  { reload: () => void }
> = ({ rowData, reload }) => {
  const { t } = useTranslation('view', { keyPrefix: 'jobs' });
  const navigate = useNavigate();
  const { user } = useUserContext();

  const deleteJob = useCallback(async () => {
    try {
      await jobService.deleteJob(rowData.id);
      reload();
    } catch (error: unknown) {
      console.error('Failed to delete', error);
    }
  }, [reload, rowData.id]);

  const copyJob = useCallback(async () => {
    try {
      const newJob = await jobService.copyJob(rowData.id);
      reload();
      navigate(generateJobWizardUrlFromFirstStep(newJob.id));
    } catch (error) {
      console.error('FAILED to Copy', error);
    }
  }, [navigate, reload, rowData.id]);

  return (
    <div className="-my-4 flex gap-1">
      {rowData.status !== 'draft' ? (
        <Button
          buttonType={ButtonType.SECONDARY}
          IconLeading={IconType.Copy}
          className={classNames('!p-3', !jobCopyFeatureFlag(user) && '!hidden')}
          onClick={(event) => {
            event.preventDefault();
            void copyJob();
          }}
          testId={`copy-job-${rowData.id}`}
          tooltip={t('duplicate')}
          iconClassName="!size-4"
        />
      ) : (
        <>
          <Link to={generateJobWizardUrl(rowData.id)} className="inline-block">
            <Button
              IconLeading={IconType.PencilSimple}
              buttonType={ButtonType.SECONDARY}
              className="!p-3"
              testId={`edit-job-${rowData.id}`}
              tooltip={t('edit')}
              iconClassName="!size-4"
            />
          </Link>
          <TrashButton
            className="!inline-block !bg-white !rounded-xl !p-3 !w-[2.625rem] !h-[2.625rem] border-blue-100 border"
            onClick={(event) => {
              event.preventDefault();
              void deleteJob();
            }}
            showConfirmation
            confirmationMessage={t('deleteJobConfirmation')}
            testId={`delete-job-${rowData.id}`}
            tooltip={t('delete')}
          />
        </>
      )}
    </div>
  );
};
